import './Certificado.css';

import {
  ActionButton,
  ActionsGroup,
  Alert,
  Checkbox,
  Chip,
  Container,
  FormattedDateTime,
  Panel,
  Table
} from '@elotech/components';
import { UserCertificateService } from 'common/service';
import { Certificado } from 'common/type/Certificado';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

type CertificateRequest = {
  filename: string | null;
  certificado: string | null;
  password: string | null;
  salvarSenha: boolean;
};

const initialValue: CertificateRequest = {
  certificado: null,
  password: null,
  filename: null,
  salvarSenha: true
};

const CertificadoPage: React.FC = () => {
  const user = useSelector((state: any) => state.auth.user);
  const [loadingTable, setLoadingTable] = useState<boolean>(false);
  const [certificados, setCertificados] = useState<Certificado[]>([]);
  const [isAddingCertificate, setIsAddingCertificate] = useState<boolean>(
    false
  );
  const [certificateRequest, setCertificateRequest] = useState<
    CertificateRequest
  >(initialValue);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const findAllCertificates = useCallback(() => {
    setLoadingTable(true);
    UserCertificateService.findAll(user.id)
      .then((certificados: any) => setCertificados(certificados.data))
      .catch(() => {
        handleAlert('error', 'Erro ao buscar certificados');
      })
      .finally(() => setLoadingTable(false));
  }, [user.id]);

  useEffect(() => {
    const userId = user?.id;
    if (userId) {
      findAllCertificates();
    }
  }, [user, findAllCertificates]);

  const addCertificate = (certificate: any) => {
    setIsAddingCertificate(true);
    setLoadingTable(true);

    UserCertificateService.save({
      contentBase64: certificate.certificado,
      password: certificate.password,
      filename: certificate.filename,
      salvarSenha: certificate.salvarSenha
    })
      .then(() => {
        handleAlert('success', 'Certificado adicionado com sucesso');
        findAllCertificates();
        resetFormState();
      })
      .catch(error => {
        handleAlert('error', 'Erro ao adicionar certificado', error);
        setLoadingTable(false);
      })
      .finally(() => {
        setIsAddingCertificate(false);
      });
  };

  const resetFormState = () => {
    setCertificateRequest(initialValue);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onRemoveCertificate = (itemToDelete: Certificado) => {
    Alert.question({
      title: `Deseja realmente excluir o certificado  ${itemToDelete.name}?`
    }).then((result: any) => {
      if (result.value) {
        setLoadingTable(true);
        UserCertificateService.deleteCertificate(itemToDelete.id)
          .then(() => {
            findAllCertificates();
            handleAlert('success', 'Certificado excluído com sucesso');
          })
          .catch(error =>
            handleAlert('error', 'Erro ao excluir certificado', error)
          )
          .finally(() => setLoadingTable(false));
      }
    });
  };

  const onFavoriteCertificate = (item: Certificado) => {
    setLoadingTable(true);

    UserCertificateService.setPrincipal(item.id)
      .then(() => {
        const certificadoPrincipalAction = item.principal
          ? 'desmarcado'
          : 'marcado';

        handleAlert(
          'success',
          `Certificado ${certificadoPrincipalAction} como principal`
        );

        findAllCertificates();
      })
      .catch(error => {
        handleAlert(
          'error',
          'Erro ao marcar certificado como principal',
          error
        );
      })
      .finally(() => setLoadingTable(false));
  };

  const handleAlert = (
    type: 'success' | 'error',
    message: string,
    error?: any
  ) => {
    if (type === 'success') {
      Alert.success({ title: message });
    } else {
      Alert.error({ title: message }, error);
    }
  };

  return (
    <Container title="Meus Certificados" icon={'user'}>
      <div className="usuario-certificado-page">
        <div className="row mb-xs">
          <div className="col-md-4 ">
            <div className="form-group">
              <label className="label" htmlFor="field">
                Certificado
              </label>
              <div
                className="file-uploader"
                style={{ backgroundColor: 'white' }}
              >
                <input
                  type="file"
                  id="upload-file-input"
                  accept=".pfx"
                  ref={fileInputRef}
                  onChange={file => {
                    const reader = new FileReader();

                    let filenameTemp: string | null = null;

                    reader.onload = () => {
                      const content = reader.result as string;
                      const partialBase64 = content.indexOf('base64,');
                      setCertificateRequest({
                        ...certificateRequest,
                        certificado: content.substring(
                          partialBase64 !== -1 ? partialBase64 + 7 : 0,
                          content.length
                        ),
                        filename: filenameTemp
                      });
                    };

                    if (file.target.files && file.target.files.length > 0) {
                      const fileReader = file.target.files[0];
                      filenameTemp = fileReader.name;
                      reader.readAsDataURL(fileReader);
                    } else {
                      setCertificateRequest({
                        ...certificateRequest,
                        certificado: null,
                        filename: null
                      });
                    }
                  }}
                  className="file-uploader-input"
                  title="Clique ou arraste para anexar"
                />

                <label htmlFor="upload-file-input" className="input">
                  {certificateRequest.filename
                    ? certificateRequest.filename
                    : 'Clique ou arraste para anexar'}
                </label>

                <label
                  htmlFor="upload-file-input"
                  className="file-uploader-icon"
                ></label>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <label className="label" htmlFor="field">
                Senha
                <div className="hint inline clean fa-question-circle module-color sm">
                  <div className="hint-content">
                    Caso não tenha a senha vinculada, será necessário inseri-la
                    sempre que utilizar o certificado.
                  </div>
                </div>
              </label>
              <input
                type="password"
                autoComplete="new-password"
                id="field"
                placeholder="Digite a senha do certificado"
                className="input"
                name="password"
                onChange={e =>
                  setCertificateRequest({
                    ...certificateRequest,
                    password: e.target.value
                  })
                }
                value={certificateRequest.password ?? ''}
              />
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <label className="label" htmlFor="salvarSenha" />
              <Checkbox
                id="salvarSenha"
                name="salvarSenha"
                size={2}
                label="Salvar a senha"
                checked={certificateRequest.salvarSenha}
                onChange={(e: any) =>
                  setCertificateRequest({
                    ...certificateRequest,
                    salvarSenha: e.target.checked
                  })
                }
              />
            </div>
          </div>
          <div className="col-md-3">
            <button
              type="submit"
              disabled={
                !certificateRequest.certificado ||
                isAddingCertificate ||
                loadingTable
              }
              style={{ marginTop: '29px' }}
              onClick={() => addCertificate(certificateRequest)}
              className="btn positive icon-left"
            >
              <em className="fa fa-plus"></em>
              Adicionar
            </button>
          </div>
        </div>

        <Panel isTable>
          <Table
            loading={loadingTable}
            values={certificados}
            highlightSuccess={item => item.principal}
            keyExtractor={item => item.id}
          >
            <Table.Column<any> header="Apelido" value={item => item.name} />
            <Table.Column<any> header="Tipo" value={item => item.tipo} />
            <Table.Column<any> header="CPF/CNPJ" value={item => item.cnpjCpf} />
            <Table.Column<any>
              header="Fim da Validade"
              value={item => (
                <FormattedDateTime
                  value={item.dataFimValidade}
                  timeZone="UTC"
                />
              )}
            />
            <Table.Column<any>
              header="Status"
              value={item => (
                <Chip
                  value={item.valid ? 'Válido' : 'Inválido'}
                  color={item.valid ? 'positive' : 'negative'}
                />
              )}
            />
            <Table.Column<any>
              header=""
              value={item => (
                <ActionsGroup>
                  <ActionButton
                    customClass={item.principal ? 'principal-button' : ''}
                    key="action-button-principal"
                    icon={'star'}
                    label={
                      item.principal
                        ? 'Desmarcar como principal'
                        : 'Marcar como principal'
                    }
                    onClick={() => {
                      onFavoriteCertificate(item);
                    }}
                  />

                  <ActionButton
                    key="action-button-remove"
                    icon={'trash'}
                    label={'Remover certificado'}
                    onClick={() => {
                      onRemoveCertificate(item);
                    }}
                  />
                </ActionsGroup>
              )}
            />
          </Table>
        </Panel>
      </div>
    </Container>
  );
};

export default CertificadoPage;
